import { type, type Type } from 'arktype';
import { dateKey } from '@zag/core/models';

export const TERMINAL_EVIDENCE_READ_PERMISSION = 'terminals:evidence:read';

export type TerminalEventProcessingState = 'pending' | 'pending-pair' | 'applied' | 'quarantined' | 'dead-letter' | 'rejected';

export const terminalEventProcessingStateSchema: Type<TerminalEventProcessingState> = type(
  "'pending' | 'pending-pair' | 'applied' | 'quarantined' | 'dead-letter' | 'rejected'"
).configure({ description: 'Processing state of a terminal scan.' });

const uuidString = type('string.uuid').configure({ description: 'UUID.' });
const nullableUuid = uuidString.or(type('null'));
const nullableName = type('string | null');

export interface BadgeLookupAssignment {
  id: string;
  personId: string;
  firstName: string;
  lastName: string;
  badge: string;
  validFromDate: string;
  validThroughDate: string | null;
  revokedAt: string | null;
  active: boolean;
}

export const badgeLookupAssignmentSchema: Type<BadgeLookupAssignment> = type({
  id: uuidString,
  personId: uuidString,
  firstName: 'string',
  lastName: 'string',
  badge: type('string').configure({ description: 'Badge number as entered for the assignment.' }),
  validFromDate: dateKey,
  validThroughDate: dateKey.or(type('null')).configure({ description: 'Inclusive last valid civil day, or null.' }),
  revokedAt: type('string | null').configure({ description: 'Revocation time (ISO 8601), or null.' }),
  active: type('boolean').configure({ description: 'Whether the assignment is valid now and not revoked.' }),
}).configure({ description: 'One assignment of the looked-up badge, with its holder.' });

export interface BadgeLookupScan {
  eventAt: string | null;
  wallTime: string;
  terminalId: string;
  terminalName: string;
  terminalLogicalId: string;
  processingState: TerminalEventProcessingState;
  quarantineReason: string | null;
  personId: string | null;
  firstName: string | null;
  lastName: string | null;
}

export const badgeLookupScanSchema: Type<BadgeLookupScan> = type({
  eventAt: type('string | null').configure({ description: 'Event time (ISO 8601), or null when not yet resolved.' }),
  wallTime: type('string').configure({ description: 'Terminal wall-clock time as delivered.' }),
  terminalId: uuidString,
  terminalName: 'string',
  terminalLogicalId: 'string',
  processingState: terminalEventProcessingStateSchema,
  quarantineReason: type('string | null').configure({ description: 'Quarantine reason, or null.' }),
  personId: nullableUuid.configure({ description: 'Attributed person, or null.' }),
  firstName: nullableName,
  lastName: nullableName,
}).configure({ description: 'One terminal scan of the looked-up badge.' });

export interface BadgeLookupResponse {
  badge: string;
  assignments: BadgeLookupAssignment[];
  scans: BadgeLookupScan[];
  unassignedScans: BadgeLookupScan[] | null;
}

export const badgeLookupResponseSchema: Type<BadgeLookupResponse> = type({
  badge: type('string').configure({ description: 'Projected 10-digit badge that was searched.' }),
  assignments: badgeLookupAssignmentSchema.array(),
  scans: badgeLookupScanSchema.array(),
  unassignedScans: badgeLookupScanSchema
    .array()
    .or(type('null'))
    .configure({ description: 'Scans of the badge without an assignment; null without terminals:evidence:read.' }),
}).configure({ description: 'Holders and recent scans of one badge.' });
